import { useState, useRef, useEffect } from "react"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuDivider,
  DropdownMenuItem,
  DropdownMenuHeader,
  DropdownMenuTitle,
} from "~/react/ui/dropdown-menu"
import { discover_search_name_index_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import type { DiscoverFilters } from "~/react/types/discover"

const t = (key: string) => window.i18n.t(key)

type ViewState = "main" | "rename" | "saveAsNew"

// Custom hook for search save logic
function useSearchSave({
  name,
  onSave,
  setHasUnsavedChanges,
  closeDropdown,
  viewId,
  filters,
  onNameChange,
}: {
  name: string
  onSave: (name: string, existingViewId?: number) => Promise<boolean>
  setHasUnsavedChanges: (value: boolean) => void
  closeDropdown: () => void
  viewId?: number
  filters: DiscoverFilters
  onNameChange: (name: string) => void
}) {
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerateName = async () => {
    setIsGenerating(true)
    try {
      const response = await fetch(discover_search_name_index_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ filters }),
      })

      if (response.ok) {
        const data = await response.json()
        onNameChange(data.name)
      } else if (response.status === 429) {
        ;(window as any).App.displayFlash("error", t("js.back.discover.save_search.rate_limited"))
      } else {
        ;(window as any).App.displayFlash("error", t("js.back.discover.save_search.generate_error"))
      }
    } catch {
      ;(window as any).App.displayFlash("error", t("js.back.discover.save_search.generate_error"))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSubmit = async () => {
    const nameToSave = name.trim()
    if (!nameToSave) {
      return
    }

    const success = await onSave(nameToSave, viewId)
    if (success) {
      setHasUnsavedChanges(false)
      closeDropdown()
    }
  }

  return { isGenerating, handleSubmit, handleGenerateName }
}

// Shared input component with sparkle button
function SearchNameInput({
  id,
  value,
  onChange,
  inputRef,
  isGenerating,
  onGenerateName,
  ariaLabel,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  isGenerating: boolean
  onGenerateName: () => void
  ariaLabel: string
}) {
  return (
    <Input
      ref={inputRef}
      id={id}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="sm"
      fullWidth
      disabled={isGenerating}
      placeholder={t("js.back.discover.save_search.name_placeholder")}
      rightElement={
        <Button
          variant="ghostLight"
          size="xs"
          iconOnly
          onClick={onGenerateName}
          disabled={isGenerating}
          aria-label={ariaLabel}
        >
          <Icon icon="sparkle" loading={isGenerating} aria-hidden="true" />
        </Button>
      }
    />
  )
}

interface SearchSaverProps {
  viewId?: number | null
  searchName?: string
  hasUnsavedChanges: boolean
  onSave: (name: string, existingViewId?: number) => Promise<boolean>
  setHasUnsavedChanges: (value: boolean) => void
  filters: DiscoverFilters
}

// Main menu component when viewId exists
function SearchSaverMainMenu({
  viewId,
  initialSearchName,
  onViewChange,
  onSave,
  setHasUnsavedChanges,
  hasUnsavedChanges,
  closeDropdown,
}: {
  viewId: number
  initialSearchName: string
  onViewChange: (view: ViewState) => void
  onSave: (name: string, existingViewId?: number) => Promise<boolean>
  setHasUnsavedChanges: (value: boolean) => void
  hasUnsavedChanges: boolean
  closeDropdown: () => void
}) {
  const handleUpdate = async () => {
    const success = await onSave(initialSearchName, viewId)
    if (success) {
      setHasUnsavedChanges(false)
      closeDropdown()
    }
  }

  return (
    <>
      {hasUnsavedChanges && (
        <DropdownMenuItem onClick={handleUpdate} closeOnClick={false}>
          <span>{t("js.back.discover.save_search.update_saved_search")}</span>
          <span className="search-saver__hint">{t("js.back.discover.save_search.recommended")}</span>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => onViewChange("rename")} closeOnClick={false}>
        {t("js.back.discover.save_search.rename_search")}
      </DropdownMenuItem>
      <DropdownMenuDivider />
      <DropdownMenuItem onClick={() => onViewChange("saveAsNew")} closeOnClick={false}>
        {t("js.back.discover.save_search.save_as_new_search")}
      </DropdownMenuItem>
    </>
  )
}

// Rename form component
function SearchSaverRenameForm({
  viewId,
  name,
  onNameChange,
  onBack,
  onSave,
  setHasUnsavedChanges,
  closeDropdown,
  inputRef,
  filters,
}: {
  viewId: number
  name: string
  onNameChange: (name: string) => void
  onBack: () => void
  onSave: (name: string, existingViewId?: number) => Promise<boolean>
  setHasUnsavedChanges: (value: boolean) => void
  closeDropdown: () => void
  inputRef: React.RefObject<HTMLInputElement | null>
  filters: DiscoverFilters
}) {
  const { isGenerating, handleSubmit, handleGenerateName } = useSearchSave({
    name,
    onSave,
    setHasUnsavedChanges,
    closeDropdown,
    viewId,
    filters,
    onNameChange,
  })

  return (
    <div>
      <DropdownMenuHeader className="">
        <Button variant="ghost" iconOnly size="xs" onClick={onBack}>
          <Icon icon="arrow-left" aria-hidden="true" />
        </Button>
        <DropdownMenuTitle>{t("js.back.discover.save_search.rename_search")}</DropdownMenuTitle>
      </DropdownMenuHeader>
      <div className="m-2">
        <label className="h-label mb-2 h-label--sm" htmlFor="rename-search-name">
          {t("js.back.discover.save_search.name_label")}
        </label>
        <SearchNameInput
          id="rename-search-name"
          value={name}
          onChange={onNameChange}
          inputRef={inputRef}
          isGenerating={isGenerating}
          onGenerateName={handleGenerateName}
          ariaLabel={t("js.back.discover.save_search.generate_name")}
        />
        <DropdownMenuDivider className="mt-3 mb-3" />
      </div>
      <Button size="sm" fullWidth className="m-2 search-saver__submit" onClick={handleSubmit} disabled={!name.trim()}>
        {t("js.back.discover.save_search.update")}
      </Button>
    </div>
  )
}

// Save as new form component
function SearchSaverSaveAsNewForm({
  name,
  onNameChange,
  onBack,
  onSave,
  setHasUnsavedChanges,
  closeDropdown,
  inputRef,
  filters,
}: {
  name: string
  onNameChange: (name: string) => void
  onBack: () => void
  onSave: (name: string, existingViewId?: number) => Promise<boolean>
  setHasUnsavedChanges: (value: boolean) => void
  closeDropdown: () => void
  inputRef: React.RefObject<HTMLInputElement | null>
  filters: DiscoverFilters
}) {
  const { isGenerating, handleSubmit, handleGenerateName } = useSearchSave({
    name,
    onSave,
    setHasUnsavedChanges,
    closeDropdown,
    filters,
    onNameChange,
  })

  return (
    <div>
      <DropdownMenuHeader className="">
        <Button variant="ghost" iconOnly size="xs" onClick={onBack}>
          <Icon icon="arrow-left" aria-hidden="true" />
        </Button>
        <DropdownMenuTitle>{t("js.back.discover.save_search.save_as_new_search")}</DropdownMenuTitle>
      </DropdownMenuHeader>
      <div className="m-2">
        <label className="h-label mb-2 h-label--sm" htmlFor="save-as-new-search-name">
          {t("js.back.discover.save_search.name_label")}
        </label>
        <SearchNameInput
          id="save-as-new-search-name"
          value={name}
          onChange={onNameChange}
          inputRef={inputRef}
          isGenerating={isGenerating}
          onGenerateName={handleGenerateName}
          ariaLabel={t("js.back.discover.save_search.generate_name")}
        />
        <DropdownMenuDivider className="mt-3 mb-3" />
      </div>
      <Button size="sm" fullWidth className="m-2 search-saver__submit" onClick={handleSubmit} disabled={!name.trim()}>
        {t("js.back.discover.save_search.save")}
      </Button>
    </div>
  )
}

// Default save form component (no viewId)
function SearchSaverDefaultForm({
  name,
  onNameChange,
  onSave,
  setHasUnsavedChanges,
  closeDropdown,
  inputRef,
  filters,
}: {
  name: string
  onNameChange: (name: string) => void
  onSave: (name: string, existingViewId?: number) => Promise<boolean>
  setHasUnsavedChanges: (value: boolean) => void
  closeDropdown: () => void
  inputRef: React.RefObject<HTMLInputElement | null>
  filters: DiscoverFilters
}) {
  const { isGenerating, handleSubmit, handleGenerateName } = useSearchSave({
    name,
    onSave,
    setHasUnsavedChanges,
    closeDropdown,
    filters,
    onNameChange,
  })

  return (
    <div className="m-2">
      <label className="h-label mb-2 h-label--sm" htmlFor="save-search-name">
        {t("js.back.discover.save_search.name_label")}
      </label>
      <SearchNameInput
        id="save-search-name"
        value={name}
        onChange={onNameChange}
        inputRef={inputRef}
        isGenerating={isGenerating}
        onGenerateName={handleGenerateName}
        ariaLabel={t("js.back.discover.save_search.generate_name")}
      />
      <DropdownMenuDivider className="mt-3 mb-3" />
      <Button size="sm" fullWidth className="search-saver__submit" onClick={handleSubmit} disabled={!name.trim()}>
        {t("js.back.discover.save_search.save")}
      </Button>
    </div>
  )
}

export function SearchSaver({
  viewId,
  searchName: initialSearchName = "",
  hasUnsavedChanges,
  onSave,
  setHasUnsavedChanges,
  filters,
}: SearchSaverProps) {
  const [name, setName] = useState(initialSearchName)
  const [viewState, setViewState] = useState<ViewState>("main")
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Update name when viewId or initialSearchName changes
  useEffect(() => {
    setName(initialSearchName)
    setViewState("main") // Reset to main view when viewId or name changes
  }, [viewId, initialSearchName])

  // Focus input when dropdown opens and we're in a form view
  useEffect(() => {
    if (!dropdownRef.current) return

    const dropdownElement = dropdownRef.current
    const triggerElement = dropdownElement.querySelector('[data-bs-toggle="dropdown"]')

    if (!triggerElement) return

    const handleShow = () => {
      // Small delay to ensure the dropdown is fully rendered
      setTimeout(() => {
        // Focus input when showing any form (default, rename, or saveAsNew)
        // but not when showing the main menu
        if (viewState === "rename" || viewState === "saveAsNew" || (!viewId && viewState === "main")) {
          inputRef.current?.focus()
        }
      }, 10)
    }

    triggerElement.addEventListener("shown.bs.dropdown", handleShow)

    return () => {
      triggerElement.removeEventListener("shown.bs.dropdown", handleShow)
    }
  }, [viewState, viewId])

  const handleBack = () => {
    setViewState("main")
    setName(initialSearchName) // Reset name to original
  }

  const closeDropdown = () => {
    const triggerElement = dropdownRef.current?.querySelector('[data-bs-toggle="dropdown"]') as HTMLElement | null
    triggerElement?.click()
    setViewState("main")
  }

  return (
    <DropdownMenu ref={dropdownRef} className="search-saver">
      <DropdownMenuTrigger asChild>
        <Button size="sm" fullWidth>
          <Icon icon="bookmark" variant={viewId ? "solid" : "regular"} aria-hidden="true" />
          <span className="search-saver__button-text">
            {viewId
              ? initialSearchName || t("js.back.discover.save_search.saved_search")
              : t("js.back.discover.save_search.save_long")}
          </span>
          {viewId && hasUnsavedChanges && (
            <span className="search-saver__unsaved">
              {" · "}
              {t("js.back.discover.save_search.unsaved")}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent size="md" className="search-saver__dropdown">
        {viewState === "main" && viewId ? (
          <SearchSaverMainMenu
            viewId={viewId}
            initialSearchName={initialSearchName}
            onViewChange={setViewState}
            onSave={onSave}
            setHasUnsavedChanges={setHasUnsavedChanges}
            hasUnsavedChanges={hasUnsavedChanges}
            closeDropdown={closeDropdown}
          />
        ) : viewState === "rename" && viewId ? (
          <SearchSaverRenameForm
            viewId={viewId}
            name={name}
            onNameChange={setName}
            onBack={handleBack}
            onSave={onSave}
            setHasUnsavedChanges={setHasUnsavedChanges}
            closeDropdown={closeDropdown}
            inputRef={inputRef as React.RefObject<HTMLInputElement>}
            filters={filters}
          />
        ) : viewState === "saveAsNew" && viewId ? (
          <SearchSaverSaveAsNewForm
            name={name}
            onNameChange={setName}
            onBack={handleBack}
            onSave={onSave}
            setHasUnsavedChanges={setHasUnsavedChanges}
            closeDropdown={closeDropdown}
            inputRef={inputRef as React.RefObject<HTMLInputElement>}
            filters={filters}
          />
        ) : (
          <SearchSaverDefaultForm
            name={name}
            onNameChange={setName}
            onSave={onSave}
            setHasUnsavedChanges={setHasUnsavedChanges}
            closeDropdown={closeDropdown}
            inputRef={inputRef as React.RefObject<HTMLInputElement>}
            filters={filters}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
